if (!customElements.get('fbt-bundle')) {
  customElements.define(
    'fbt-bundle',
    class FbtBundle extends HTMLElement {
      constructor() {
        super();
        this.config = null;
        this.baseDiscountPercent = 0;
        this.discountPercent = 0;
        this.enableDiscount = true;
        this.discountScope = 'entire_bundle';
        this.tiers = null;
        this.variantChangeUnsubscriber = undefined;
      }

      connectedCallback() {
        const configEl = this.querySelector('[data-fbt-config]');
        if (!configEl) return;

        try {
          this.config = JSON.parse(configEl.textContent);
        } catch (e) {
          console.error('FBT: invalid config', e);
          return;
        }

        this.enableDiscount =
          this.config.enableDiscount === true || this.dataset.enableDiscount === 'true';

        this.baseDiscountPercent = parseFloat(
          this.dataset.discountPercent || this.config.discountPercent || 0
        );
        if (!this.enableDiscount) {
          this.baseDiscountPercent = 0;
        }
        this.discountPercent = this.baseDiscountPercent || 0;
        this.discountScope = this.dataset.discountScope || this.config.discountScope || 'entire_bundle';
        this.tiers = this.config.tiers || null;
        this.addonMode = this.dataset.addonMode || 'fixed';
        this.addonDisplayCount = parseInt(this.dataset.addonDisplayCount || '0', 10) || 0;
        this.fixedCount = parseInt(this.dataset.fixedCount || '0', 10) || 0;

        this.submitBtn = this.querySelector('[data-fbt-submit]');
        this.errorEl = this.querySelector('[data-fbt-error]');

        this.applyAddonMode();

        this.querySelectorAll('[data-fbt-variant-select], [data-fbt-checkbox]').forEach((el) => {
          el.addEventListener('change', () => this.updatePrices());
        });

        if (this.submitBtn) {
          this.submitBtn.addEventListener('click', () => this.addBundleToCart());
        }

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event?.data?.sectionId !== this.dataset.sectionId) return;
            this.syncMainVariantFromPage(event.data.variant);
          });
        }

        this.syncMainVariantFromForm();
        this.updatePrices();
      }

      shuffle(arr) {
        const cloned = [...arr];
        for (let i = cloned.length - 1; i > 0; i -= 1) {
          const j = Math.floor(Math.random() * (i + 1));
          [cloned[i], cloned[j]] = [cloned[j], cloned[i]];
        }
        return cloned;
      }

      updatePlusVisibility() {
        this.querySelectorAll('[data-fbt-plus]').forEach((plus) => {
          const item = plus.nextElementSibling;
          const visible = item?.matches('[data-fbt-row="addon"]') && !item.classList.contains('is-hidden');
          plus.classList.toggle('is-hidden', !visible);
        });
      }

      applyAddonMode() {
        const rows = [...this.querySelectorAll('[data-fbt-row="addon"]')];
        if (!rows.length) return;

        // 先按 product_id 去重（手动配置优先）
        const seenProductIds = new Set();
        const dedupedRows = [];
        rows.forEach((row) => {
          const pid = row.dataset.productId;
          if (!pid || !seenProductIds.has(pid)) {
            seenProductIds.add(pid);
            dedupedRows.push(row);
          } else {
            row.classList.add('is-hidden');
            const checkbox = row.querySelector('[data-fbt-checkbox]');
            if (checkbox) checkbox.checked = false;
          }
        });

        const manualRows = dedupedRows.filter((row) => row.dataset.addonSource === 'manual');
        const collectionRows = dedupedRows.filter((row) => row.dataset.addonSource === 'collection');
        const maxCount = this.addonDisplayCount > 0 ? this.addonDisplayCount : dedupedRows.length;
        let chosenRows = [];

        if (this.addonMode === 'random') {
          const pool = collectionRows.length ? collectionRows : manualRows;
          chosenRows = this.shuffle(pool).slice(0, maxCount);
        } else if (this.addonMode === 'mixed') {
          const keepCount = Math.min(this.fixedCount, manualRows.length, maxCount);
          const fixedPart = manualRows.slice(0, keepCount);
          const need = Math.max(0, maxCount - fixedPart.length);

          let randomPool = collectionRows.length ? collectionRows : manualRows.slice(keepCount);
          let randomPart = this.shuffle(randomPool).slice(0, need);

          // 补足数量
          if (randomPart.length < need) {
            const chosenIds = new Set([...fixedPart, ...randomPart].map((row) => row.dataset.productId));
            const fallbackPool = dedupedRows.filter((row) => !chosenIds.has(row.dataset.productId));
            const fill = this.shuffle(fallbackPool).slice(0, need - randomPart.length);
            randomPart = [...randomPart, ...fill];
          }
          chosenRows = [...fixedPart, ...randomPart];
        } else {
          chosenRows = manualRows.slice(0, maxCount);
        }

        const chosenSet = new Set(chosenRows);
        dedupedRows.forEach((row) => {
          const visible = chosenSet.has(row);
          row.classList.toggle('is-hidden', !visible);
          const checkbox = row.querySelector('[data-fbt-checkbox]');
          if (checkbox && !visible) {
            checkbox.checked = false;
          }
        });

        this.updatePlusVisibility();
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
      }

      syncMainVariantFromForm() {
        const form = document.getElementById(this.dataset.productFormId);
        const variantInput = form?.querySelector('[name="id"]');
        if (!variantInput?.value) return;

        const mainSelect = this.querySelector('[data-fbt-row="main"] [data-fbt-variant-select]');
        if (mainSelect && mainSelect.tagName === 'SELECT') {
          mainSelect.value = variantInput.value;
        }
      }

      syncMainVariantFromPage(variant) {
        if (!variant?.id) return;

        const mainRow = this.querySelector('[data-fbt-row="main"]');
        const mainSelect = mainRow?.querySelector('[data-fbt-variant-select]');
        if (mainSelect && mainSelect.tagName === 'SELECT') {
          mainSelect.value = String(variant.id);
        }

        const mainImage = mainRow?.querySelector('[data-fbt-main-image]');
        if (mainImage && variant.featured_image?.src) {
          mainImage.src = variant.featured_image.src;
        }

        this.updatePrices();
      }

      getSelectedVariantData(row) {
        const select = row.querySelector('[data-fbt-variant-select]');
        if (!select) return null;

        if (select.tagName === 'SELECT') {
          const option = select.options[select.selectedIndex];
          if (!option || option.disabled) return null;
          return {
            id: parseInt(option.value, 10),
            price: parseInt(option.dataset.price, 10),
            compareAt: parseInt(option.dataset.compareAt, 10) || 0,
            available: option.dataset.available !== 'false',
          };
        }

        return {
          id: parseInt(select.value, 10),
          price: parseInt(select.dataset?.price || '0', 10),
          compareAt: parseInt(select.dataset?.compareAt || '0', 10) || 0,
          available: true,
        };
      }

      isRowActive(row) {
        if (row.classList.contains('is-hidden')) return false;
        if (row.dataset.fbtRow === 'main') return true;
        const checkbox = row.querySelector('[data-fbt-checkbox]');
        return checkbox?.checked && !checkbox.disabled;
      }

      shouldApplyDiscount(row) {
        if (this.discountScope === 'addons_only') {
          return row.dataset.fbtRow === 'addon';
        }
        return true;
      }

      getEffectivePercent(activeCount) {
        let best = 0;
        let hasTierConfig = false;

        if (!this.enableDiscount) {
          return 0;
        }

        if (this.tiers) {
          Object.values(this.tiers).forEach((tier) => {
            const qty = parseInt(tier?.qty ?? 0, 10);
            const pct = parseFloat(tier?.percent ?? 0);
            if (!qty || !pct) return;
            hasTierConfig = true;
            if (activeCount >= qty && pct > best) {
              best = pct;
            }
          });
        }

        // 配置了分级折扣时，未达到任一档位则不打折；
        // 未配置分级折扣时，回退到默认折扣比例。
        if (!hasTierConfig) {
          best = this.baseDiscountPercent || 0;
        }

        return best;
      }

      calcDiscountedPrice(basePrice, applyDiscount) {
        if (!applyDiscount || !this.discountPercent) return basePrice;
        return Math.round(basePrice * (1 - this.discountPercent / 100));
      }

      getRowTotals(variantData, applyBundleDiscount) {
        if (!variantData) return { original: 0, displayOriginal: 0, final: 0, salePrice: 0 };

        const salePrice = variantData.price;
        const compareAt = variantData.compareAt || 0;
        const hasCompareAt = compareAt > salePrice;
        const final = applyBundleDiscount
          ? this.calcDiscountedPrice(salePrice, true)
          : salePrice;
        const original = hasCompareAt ? compareAt : 0;
        let displayOriginal = 0;
        if (hasCompareAt) {
          displayOriginal = compareAt;
        } else if (applyBundleDiscount && final < salePrice) {
          displayOriginal = salePrice;
        }

        return { original, displayOriginal, final, salePrice, compareAt };
      }

      getSavePercent(original, final) {
        if (!original || original <= final) return 0;
        return Math.round((1 - final / original) * 100);
      }

      formatMoney(cents) {
        if (typeof cents !== 'number' || isNaN(cents)) return '';
        const format = this.config?.moneyFormat || '${{amount}}';
        const amount = (cents / 100).toFixed(2);
        const amountWithComma = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        const formatted = window.Shopify?.formatMoney
          ? window.Shopify.formatMoney(cents, format)
          : format
              .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountWithComma)
              .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(cents / 100)))
              .replace(/\{\{\s*amount\s*\}\}/g, amount);
        return String(formatted).replace(/[\r\n]+/g, ' ');
      }

      setMoney(el, cents) {
        if (!el) return;
        const value = this.formatMoney(cents);
        if (value.includes('<')) {
          el.innerHTML = value;
        } else {
          el.textContent = value;
        }
      }

      updateRowPriceDisplay(row, variantData, applyBundleDiscount) {
        const compareEl = row.querySelector('[data-fbt-compare]');
        const priceEl = row.querySelector('[data-fbt-price]');
        const badgeEl = row.querySelector('[data-fbt-badge]');
        if (!variantData || !priceEl) return;

        const applyBundle =
          this.enableDiscount && applyBundleDiscount && this.discountPercent > 0;
        const { displayOriginal, final } = this.getRowTotals(variantData, applyBundle);

        if (badgeEl) {
          if (applyBundle) {
            badgeEl.textContent = `-${Math.round(this.discountPercent)}%`;
            badgeEl.style.display = '';
          } else {
            badgeEl.style.display = 'none';
          }
        }

        if (compareEl) {
          if (displayOriginal > final) {
            this.setMoney(compareEl, displayOriginal);
            compareEl.style.display = '';
          } else {
            compareEl.textContent = '';
            compareEl.style.display = 'none';
          }
        }

        this.setMoney(priceEl, final);
      }

      updatePrices() {
        let totalOriginal = 0;
        let totalFinal = 0;
        let hasUnavailable = false;
        const rows = [...this.querySelectorAll('[data-fbt-row]')];
        const activeRows = rows.filter((row) => this.isRowActive(row));
        const activeCount = activeRows.length;
        const activeAddonCount = rows.filter(
          (row) => row.dataset.fbtRow === 'addon' && this.isRowActive(row)
        ).length;

        // 分级折扣按“已选择配件件数”计算
        this.discountPercent = this.getEffectivePercent(activeAddonCount);
        const bundleQualifies = this.discountPercent > 0 && activeAddonCount >= 1;
        const percentLabel = `-${Math.round(this.discountPercent)}%`;

        rows.forEach((row) => {
          const active = this.isRowActive(row);
          const variantData = this.getSelectedVariantData(row);
          const applyDiscount = active && bundleQualifies && this.shouldApplyDiscount(row);

          if (active) {
            if (!variantData?.available) hasUnavailable = true;
          }

          this.updateRowPriceDisplay(row, variantData, applyDiscount);

          if (active && variantData) {
            const applyBundle =
              this.enableDiscount && applyDiscount && this.discountPercent > 0;
            const { original, final } = this.getRowTotals(variantData, applyBundle);
            totalOriginal += original;
            totalFinal += final;
          }
        });

        const totalCompareEl = this.querySelector('[data-fbt-total-compare]');
        const totalPriceEl = this.querySelector('[data-fbt-total-price]');
        const totalBadgeEl = this.querySelector('[data-fbt-total-badge]');

        if (totalCompareEl) {
          if (totalOriginal > totalFinal) {
            this.setMoney(totalCompareEl, totalOriginal);
            totalCompareEl.style.display = '';
          } else {
            totalCompareEl.textContent = '';
            totalCompareEl.style.display = 'none';
          }
        }

        if (totalPriceEl) {
          this.setMoney(totalPriceEl, totalFinal);
        }

        if (totalBadgeEl) {
          if (this.enableDiscount && bundleQualifies && this.discountPercent > 0) {
            totalBadgeEl.textContent = percentLabel;
            totalBadgeEl.style.display = '';
          } else {
            totalBadgeEl.style.display = 'none';
          }
        }

        if (this.submitBtn) {
          const disabled = activeCount < 1 || hasUnavailable;
          this.submitBtn.disabled = disabled;
          this.submitBtn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
        }
      }

      getBundleId() {
        return `fbt-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      }

      buildLineItemProperties(role) {
        return {
          _fbt_bundle: '1',
          _fbt_bundle_id: this.bundleId,
          _fbt_discount_percent: String(this.enableDiscount ? this.discountPercent : 0),
          _fbt_discount_scope: this.discountScope,
          _fbt_role: role,
        };
      }

      getSelectedItems() {
        const entries = [];

        this.querySelectorAll('[data-fbt-row]').forEach((row) => {
          if (!this.isRowActive(row)) return;
          const variantData = this.getSelectedVariantData(row);
          if (!variantData?.id || !variantData.available) return;

          entries.push({ row, variantData });
        });

        const bundleProductIds = entries
          .map(({ row }) => row.dataset.productId)
          .filter(Boolean)
          .join(',');

        return entries.map(({ row, variantData }) => ({
          id: variantData.id,
          quantity: 1,
          properties: {
            ...this.buildLineItemProperties(row.dataset.fbtRow === 'main' ? 'main' : 'addon'),
            '__frequently-bought-together-added': bundleProductIds,
          },
        }));
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message;
        this.errorEl.classList.toggle('hidden', !message);
      }

      setLoading(loading) {
        if (!this.submitBtn) return;

        const spinner = this.submitBtn.querySelector('.loading__spinner');
        this.submitBtn.classList.toggle('loading', loading);
        this.submitBtn.setAttribute('aria-disabled', loading ? 'true' : 'false');

        if (spinner) {
          spinner.classList.toggle('hidden', !loading);
        }
      }

      async addBundleToCart() {
        this.bundleId = this.getBundleId();
        const items = this.getSelectedItems();
        if (!items.length) return;

        this.showError('');
        this.setLoading(true);

        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const body = { items };

        if (cart?.getSectionsToRender) {
          body.sections = cart.getSectionsToRender().map((s) => s.id).join(',');
          body.sections_url = window.location.pathname;
        }

        try {
          const response = await fetch(window.routes.cart_add_url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify(body),
          });

          const data = await response.json();

          if (data.status || data.message) {
            this.showError(data.description || data.message || window.cartStrings?.error || 'Could not add to cart');
            if (typeof publish === 'function') {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'fbt-bundle',
                errors: data.description,
              });
            }
            return;
          }

          if (cart?.renderContents) {
            cart.classList.remove('is-empty');
            cart.querySelector('cart-drawer-items')?.classList.remove('is-empty');

            if (cart.getSectionsToRender) {
              const sectionIds = cart.getSectionsToRender().map((section) => section.id).join(',');
              const sectionsResponse = await fetch(`${window.location.pathname}?sections=${sectionIds}`);
              if (sectionsResponse.ok) {
                data.sections = await sectionsResponse.json();
              }
            }

            cart.setActiveElement?.(this.submitBtn);
            cart.renderContents(data);
          } else {
            window.location = window.routes.cart_url;
            return;
          }

          if (typeof publish === 'function') {
            await publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'fbt-bundle',
              cartData: data,
            });
          }
        } catch (err) {
          console.error('FBT add to cart error', err);
          this.showError(window.cartStrings?.error || 'Could not add to cart');
        } finally {
          this.setLoading(false);
        }
      }
    }
  );
}
